import React, { useEffect, useState } from 'react';
import Modal from '../../../../components/shareable/modal';
import { formatPhoneNumber } from '../../../../utils/formatPhoneNumber';

const emptyLocation = { location: '', primary_contact: '', primary_contact_number: '', email: '' };

const AddContactModal = ({ isOpen, onClose, onConfirm, customerName = '' }) => {
  const [form, setForm] = useState(emptyLocation);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setForm(emptyLocation);
      setErrors({});
      setSaving(false);
    }
  }, [isOpen]);

  const update = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const validate = () => {
    const next = {};
    if (!form.location.trim()) next.location = 'Location is required';
    if (!form.primary_contact.trim()) next.primary_contact = 'Contact name is required';
    if (form.email && !/\S+@\S+\.\S+/.test(form.email)) next.email = 'Email is invalid';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate() || saving) return;
    setSaving(true);
    try {
      await onConfirm({
        location: form.location.trim(),
        primary_contact: form.primary_contact.trim(),
        primary_contact_number: form.primary_contact_number,
        email: form.email.trim()
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} width="xl" onClose={onClose}>
      <div className="flex flex-col">
        <h2 className="text-lg font-semibold mb-1">Add Contact / Location</h2>
        {customerName && (
          <p className="text-xs text-gray-500 mb-3">for <span className="font-medium text-gray-700">{customerName}</span></p>
        )}

        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="block text-xs font-medium mb-1">
              Location <span className="text-red-500">*</span>
            </label>
            <textarea
              value={form.location}
              onChange={(e) => update('location', e.target.value)}
              rows={2}
              className={`w-full p-2 border rounded text-sm ${errors.location ? 'border-red-500' : ''}`}
              placeholder="Enter location (e.g. 123 Main St, City, State)"
            />
            {errors.location && <p className="mt-1 text-xs text-red-500">{errors.location}</p>}
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium mb-1">
                Primary Contact <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={form.primary_contact}
                onChange={(e) => update('primary_contact', e.target.value)}
                className={`w-full p-2 border rounded text-sm ${errors.primary_contact ? 'border-red-500' : ''}`}
                placeholder="Contact name"
              />
              {errors.primary_contact && <p className="mt-1 text-xs text-red-500">{errors.primary_contact}</p>}
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Contact Number</label>
              <input
                type="text"
                value={formatPhoneNumber(form.primary_contact_number)}
                onChange={(e) => update('primary_contact_number', e.target.value.replace(/\D/g, '').slice(0, 10))}
                className="w-full p-2 border rounded text-sm"
                placeholder="(123) 456-7890"
              />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Email</label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => update('email', e.target.value)}
                className={`w-full p-2 border rounded text-sm ${errors.email ? 'border-red-500' : ''}`}
                placeholder="email@example.com"
              />
              {errors.email && <p className="mt-1 text-xs text-red-500">{errors.email}</p>}
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-3 border-t">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="px-5 py-1.5 bg-gray-300 rounded hover:bg-gray-400 text-sm"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-5 py-1.5 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 text-sm"
            >
              {saving ? 'Saving...' : 'Add Contact'}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default AddContactModal;
